/**
 * Read the current URL's query string as a flat record. `useCursorList`
 * serializes the whole list view (search text `q`, `sort`/`dir`, and each
 * filter — arrays as CSV) into the URL so a filtered/sorted view survives Back
 * and is shareable/bookmarkable. We read/write the URL directly via the
 * History API rather than TanStack Router's typed search, because the filter
 * set differs per list and a permissive `validateSearch` destabilizes the
 * router's route-tree type inference.
 */
export function readUrlParams(): Record<string, string> {
  if (typeof window === "undefined") return {};
  return Object.fromEntries(new URLSearchParams(window.location.search));
}

/**
 * Replace the current URL's query string in place (no history entry, no
 * navigation) from a flat string record. Empty record clears the query.
 */
export function writeUrlParams(params: Record<string, string>): void {
  if (typeof window === "undefined") return;
  const qs = new URLSearchParams(params).toString();
  const url = window.location.pathname + (qs ? `?${qs}` : "");
  window.history.replaceState(window.history.state, "", url);
}

/**
 * Snapshot the current list view's filter query string as a param bag to carry
 * into a detail page (as `lf`), so the detail's "back to catalog" crumb can
 * return to the exact filtered/sorted view. Empty (`{}`) when the list has no
 * active filters, keeping the detail URL clean. Read at click time from
 * `window.location`, where `useCursorList` mirrors the live filter state.
 */
export function carryListSearch(): { lf?: string } {
  if (typeof window === "undefined") return {};
  const qs = window.location.search.replace(/^\?/, "");
  return qs ? { lf: qs } : {};
}

/** Read a URL search value as a string, or undefined when absent/blank. */
export function readSearchString(value: unknown): string | undefined {
  return typeof value === "string" && value !== "" ? value : undefined;
}

/** Read a CSV URL search value as a string array (empty when absent). */
export function readSearchList(value: unknown): string[] {
  const raw = readSearchString(value);
  return raw ? raw.split(",").filter(Boolean) : [];
}
